import Styles from './LPath.module.scss';
import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import ProductGridCard from 'components/global/ProductCards/ProductGridCard/ProductGridCard';
import ProductListCard from 'components/global/ProductCards/ProductListCard/ProductListCard';
import PurchaseGridCard from 'components/global/PurchaseCards/PurchaseGridCard/PurchaseGridCard';
import PurchaseListCard from 'components/global/PurchaseCards/PurchaseListCard/PurchaseListCard';
import SearchBar from 'components/global/SearchBar/SearchBar';
import { useDebounce, usePurchases, Message, AuthContext, ViewContext } from 'components/lib';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { PaymentPending } from 'views/account/paymentPending';
import { Notification } from 'components/notifications/notification';
import FilterBar from 'components/FilterBar/FilterBar';

import {
    Tabs, Tab, Box, Pagination, ToggleButton, ToggleButtonGroup,
    Typography, Divider
} from '@mui/material';
import { GridView, ViewList, SearchOff, PlaylistAddCheck, AccountBalance, School } from '@mui/icons-material';
import { ToastContainer } from 'react-toastify';

export function LPathView(props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const context = useContext(AuthContext);
    const viewContext = useContext(ViewContext);

    const [currentTab, setCurrentTab] = useState(searchParams.get('tab') || 'my-certs');

    const [viewType, setViewType] = useState('grid');
    const [searchValue, setSearchValue] = useState('');
    const [searchActive, setSearchActive] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [certs, setCerts] = useState([]);
    const [loading, setLoading] = useState(false);

    const [certIsPartOf, setCertIsPartOf] = useState([]);

    const debouncedSearchValue = useDebounce(searchValue, 500, 3);

    const [appliedTypes, setAppliedTypes] = useState([]);
    const [appliedTags, setAppliedTags] = useState([]);
    const [tagsOptions, setTagsOptions] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [pageSize, setPageSize] = useState(12);

    const hasCalculated = useRef(false);
    const currentCols = useRef(0);
    const gridRef = useRef(null);

    const fetchTags = async () => {
        if (tagsOptions.length > 0) return;

        try {
            setTagsLoading(true);
            const response = await axios.get('/api/tags');
            setTagsOptions(response?.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch tags', error.message);
        } finally {
            setTagsLoading(false);
        }
    };

    // Reset hasInitialized when tab or viewType changes
    useEffect(() => {
        hasCalculated.current = false;
        currentCols.current = 0;
    }, [viewType, currentTab]);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            if (viewType === 'grid' && pageSize === 0) return;
            setLoading(true);
            try {
                let url;
                switch (currentTab) {
                    case 'my-certs':
                        url = '/api/user/lpath';
                        break;
                    case 'purchase':
                        url = '/api/lpath/available';
                        break;
                    case 'org':
                        url = '/api/lpath/org';
                        break;
                    default:
                        url = '/api/user/lpath';
                }

                const response = await axios.get(url, {
                    params: {
                        page: pageNumber,
                        pageSize: viewType === 'grid' ? pageSize : 10,
                        search: debouncedSearchValue.trim(),
                        ...(appliedTypes.length > 0 && { type: appliedTypes[0] }),
                        ...(appliedTags.length > 0 && { tags: appliedTags.join(',') })
                    }
                });

                if (cancelled) return;

                const data = response?.data?.data;
                setCerts(data);

                const totalCount = response?.data?.total || response?.data?.totalCount ||
                    (Array.isArray(data) ? data.length : 0);
                setTotalPages(Math.ceil(totalCount / pageSize));
            } catch (err) {
                if (!cancelled) viewContext.notification.show('Error fetching data', 'error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();

        return () => { cancelled = true };
    }, [
        currentTab,
        pageNumber,
        debouncedSearchValue,
        appliedTypes,
        appliedTags,
        pageSize,
        viewType
    ]);

    useEffect(() => {
        const calculateColumns = () => {
            const container = gridRef.current;
            if (!container) return;

            const containerWidth = container.clientWidth;
            // get ONE card (even from small data)
            const card = container.querySelector(':scope > *');
            if (!card) return;

            const cardWidth = card.getBoundingClientRect().width;

            if (!cardWidth) return;

            const cols = Math.max(1, Math.floor(containerWidth / cardWidth));

            if (cols === currentCols.current) return;

            currentCols.current = cols;

            setPageSize(cols * 4);
        };

        const timer = setTimeout(calculateColumns, 100);

        window.addEventListener('resize', calculateColumns);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', calculateColumns);
        };
    }, [viewType, certs.length]);

    useEffect(() => {
        setPageNumber(1)
    }, [pageSize])

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        setSearchParams({ tab: newValue });
        setPageNumber(1);
        setSearchValue('');
        setSearchActive(false);
    };

    const handlePageChange = (event, value) => {
        setPageNumber(value);
    };

    const handleViewChange = (event, nextView) => {
        if (nextView !== null) {
            setViewType(nextView);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        let cleanedText = pastedText
            .replace(/^\s+/, "")
            .replace(/\s{2,}/g, " ")
            .trim();

        if (cleanedText) {
            setSearchValue(cleanedText);
            setPageNumber(1);
        }
    };

    const handleUserCertClick = async (cert) => {
        if (cert.is_internship === 1) {
            setCertIsPartOf([]);
            try {
                const { data } = await axios.get('/api/labs/internships/all', { params: { certId: cert.lpath_id } });
                setCertIsPartOf(data.data ?? []);
            } catch (error) {
                viewContext.notification.show(
                    'Failed to fetch the internships',
                    'error',
                    true
                );
            }
        } else {
            navigate(`/lpaths/${cert.lpath_id}/learningpath`);
        }
    }

    const handleCertTooltipClick = (itemId, isInternship = null) => {
        if (isInternship === 1) {
            navigate(`/internships/${itemId}/info`);
            return;
        } else {
            navigate(`/lpaths/${itemId}/learningpath`);
            return;
        }
    }

    const handleCardClick = (cert) => {
        switch (currentTab) {
            case 'my-certs':
                handleUserCertClick(cert);
                break
            case 'purchase':
                navigate(`/lpaths/info/${cert.id}`)
                break
            case 'org':
                navigate(`/lpaths/info/${cert.id}`)
                break
            default:
                navigate(`/lpaths/${cert.lpath_id}/learningpath`);
        }
    };

    const renderLabCards = () => {
        const isMyCerts = currentTab === 'my-certs';
        const mode = currentTab === 'org' ? 'enroll' :
            (context?.user?.plan === 'free' || context?.user?.plan === 'hybrid') ? 'purchase' : 'enroll';
        return certs.map((cert) => {
            const isInternship = cert?.is_internship === 1;
            if (isMyCerts) {
                return viewType === 'grid' ? (
                    <ProductGridCard
                        data={cert}
                        key={cert.id}
                        onClick={handleCardClick}
                        button_text={cert.buttonText}
                        showProgressBar={true}
                        showInfoChips={true}
                        showMedalChips={false}
                        showBadge={true}
                        tooltipMessage={props.t('LearningPath.UserLearningPaths.tooltip')}
                        isPartOfOtherProducts={certIsPartOf}
                        onCertTooltipClick={handleCertTooltipClick}
                        isInternship={isInternship}
                    />
                ) : (
                    <ProductListCard
                        data={cert}
                        key={cert.id}
                        onClick={handleCardClick}
                        button_text={cert.buttonText}
                        showProgressBar={true}
                        showInfoChips={true}
                        showMedalChips={false}
                        showBadge={true}
                        tooltipMessage={props.t('LearningPath.UserLearningPaths.tooltip')}
                        isPartOfOtherProducts={certIsPartOf}
                        onCertTooltipClick={handleCertTooltipClick}
                        isInternship={isInternship}
                    />
                );
            } else {
                return viewType === 'grid' ? (
                    <PurchaseGridCard
                        key={cert.id}
                        data={cert}
                        onCardClick={handleCardClick}
                        mode={mode}
                        type="cert"
                        showBadge={true}
                    />
                ) : (
                    <PurchaseListCard
                        key={cert.id}
                        data={cert}
                        onCardClick={handleCardClick}
                        mode={mode}
                        type="cert"
                        showBadge={true}
                    />
                );
            }
        });
    }

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Notification call='cert' />
            {context?.user?.subscription === 'active' ? (
                context?.user?.onboarded ? (
                    <Message
                        closable
                        title={props.t('LearningPath.UserLearningPaths.message.title')}
                        text={props.t('LearningPath.UserLearningPaths.message.text')}
                        type="info"
                    />
                ) : (
                    <Message
                        title={'Profile Setup Pending'}
                        text={'Please complete your profile setup to unlock all features and get started.'}
                        type="warning"
                        buttonText={'Setup Profile'}
                        buttonLink={'/account'}
                    />
                )
            ) : (
                <PaymentPending />
            )}
            <div className={Styles.cardContainer}>
                <div className={Styles.tabContainer}>
                    <BreadCrumbs
                        onClickPaths={['']}
                        crumbs={['Certifications']}
                    />
                </div>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        aria-label="certs tabs"
                    >
                        <Tab label="My Certifications" value="my-certs" />
                        <Tab label="Available Certifications" value="purchase" />
                        {context?.user?.plan === 'hybrid' && (
                            <Tab label="Organization Certifications" value="org" />
                        )}
                    </Tabs>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "1em",
                        mb: 2
                    }}
                >
                    <SearchBar
                        value={searchValue}
                        setValue={(val) => {
                            setSearchValue(val);
                            setPageNumber(1);
                            setSearchActive(val.length > 0);
                        }}
                        placeholder={props.t('Labs.LabsPurchase.search.placeholder')}
                        searchActive={searchActive}
                        onClear={() => {
                            setSearchValue('');
                            setPageNumber(1);
                            setSearchActive(false);
                        }}
                        onPaste={handlePaste}
                    />
                    <ToggleButtonGroup
                        value={viewType}
                        exclusive
                        onChange={handleViewChange}
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ViewList fontSize="medium" />
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label="grid view">
                            <GridView fontSize="medium" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {/* FILTER ROW */}
                <FilterBar
                    typeOptions={[{ label: "Custom Certificates", value: "custom_resource" }]}
                    tagOptions={tagsOptions.map(t => ({
                        label: t.tag_name,
                        value: t.tag_name.toLowerCase()
                    }))}
                    appliedTypes={appliedTypes}
                    setAppliedTypes={setAppliedTypes}
                    appliedTags={appliedTags}
                    setAppliedTags={setAppliedTags}
                    quickTags={["Data Science", "Full Stack", "AI", "Security"]}
                    onFetchTags={fetchTags}
                />

                {certs?.length > 0 && currentTab !== 'my-certs' && (
                    <Typography variant="h7" sx={{ display: 'flex', gap: '16px', padding: '24px 0px', mb: '-15px' }}>
                        {currentTab === 'org' ?
                            props.t('LearningPath.LPathPurchase.info.enroll') :
                            (context?.user?.plan === 'free' || context?.user?.plan === 'hybrid') ?
                                props.t('LearningPath.LPathPurchase.info.purchase') :
                                props.t('LearningPath.LPathPurchase.info.enroll')
                        }
                    </Typography>
                )}

                {/* Cards */}
                {loading ? (
                    <LoadingBackdrop isOpen={true} />
                ) : certs.length > 0 ? (
                    <div style={{ width: "100%" }}>
                        <div
                            ref={viewType === 'grid' ? gridRef : null}
                            className={viewType === 'grid' ? Styles.gridWrapper : Styles.listWrapper}
                        >
                            {renderLabCards()}
                        </div>
                    </div>
                ) : (searchActive ? (
                    <div className={Styles.emptyState}>
                        <SearchOff sx={{ fontSize: 56 }} className={Styles.icon} />
                        <div className={Styles.enrollMessage}>{props.t('Labs.LabsPurchase.message2')}</div>
                    </div>
                ) : (
                    <div className={`${Styles.emptyState} ${Styles.emptyStateBlue}`}>
                        {currentTab === 'my-certs' ? (
                            <School sx={{ fontSize: 56 }} className={Styles.icon} />
                        ) : currentTab === 'org' ? (
                            <AccountBalance sx={{ fontSize: 56 }} className={Styles.icon} />
                        ) : (
                            <PlaylistAddCheck sx={{ fontSize: 56 }} className={Styles.icon} />
                        )}
                        <div className={Styles.enrollMessage}>
                            {currentTab === 'my-certs' ?
                                "Your enrolled certifications will show up here. Click on available certifications to get started."
                                : currentTab === 'org' ?
                                    'Your organization certifications will show up here. Currently there are no organizational certifications'
                                    : "You are currently enrolled in all available certifications on the platform. Please check back for new certifications soon"
                            }
                        </div>
                    </div>
                ))}

                {/* Pagination */}
                {!loading && certs.length > 0 && (
                    <div className={Styles.pageBtnContainer}>
                        <Pagination
                            count={totalPages}
                            color="primary"
                            variant='outlined'
                            page={pageNumber}
                            onChange={handlePageChange}
                            showFirstButton
                            showLastButton
                        />
                    </div>
                )}
            </div>
        </>
    );
}